import {
  $getSelection,
  $isRangeSelection,
  LexicalNode,
  $createParagraphNode,
  $insertNodes,
} from 'lexical';
import {
  $isTableNode,
  $isTableRowNode,
  $isTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $createTableCellNode,
  TableNode,
  TableRowNode,
  TableCellNode,
  TableCellHeaderStates,
} from '@lexical/table';

export type TableContext = {
  tableNode: TableNode;
  tableRowNode: TableRowNode;
  tableCellNode: TableCellNode;
  rowIndex: number;
  columnIndex: number;
};

/**
 * Gets the table context if the current selection is inside a table
 */
export function $getTableContext(): TableContext | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return null;
  }

  const anchorNode = selection.anchor.getNode();

  // Start from the anchor node and traverse up to find table structures
  let currentNode: LexicalNode | null = anchorNode;
  let tableCellNode: TableCellNode | null = null;
  let tableRowNode: TableRowNode | null = null;
  let tableNode: TableNode | null = null;

  // Traverse up the tree to find table nodes
  while (currentNode) {
    if ($isTableCellNode(currentNode) && !tableCellNode) {
      tableCellNode = currentNode;
    } else if ($isTableRowNode(currentNode) && !tableRowNode) {
      tableRowNode = currentNode;
    } else if ($isTableNode(currentNode) && !tableNode) {
      tableNode = currentNode;
      break;
    }
    currentNode = currentNode.getParent();
  }

  // If we found all required nodes, calculate indices
  if (tableNode && tableRowNode && tableCellNode) {
    const rows = tableNode.getChildren() as TableRowNode[];
    const rowIndex = rows.indexOf(tableRowNode);

    const cells = tableRowNode.getChildren() as TableCellNode[];
    const columnIndex = cells.indexOf(tableCellNode);

    return {
      tableNode,
      tableRowNode,
      tableCellNode,
      rowIndex,
      columnIndex,
    };
  }

  return null;
}

/**
 * Checks if the current selection is inside a table
 */
export function $isSelectionInTable(): boolean {
  return $getTableContext() !== null;
}

/**
 * Creates an empty table cell with a paragraph node
 */
export function $createEmptyTableCell(
  isHeader: boolean = false,
): TableCellNode {
  const tableCellNode = $createTableCellNode(
    isHeader ? TableCellHeaderStates.ROW : TableCellHeaderStates.NO_STATUS,
  );

  // Add an empty paragraph to the cell
  const paragraphNode = $createParagraphNode();
  tableCellNode.append(paragraphNode);

  return tableCellNode;
}

/**
 * Inserts a new table at the current cursor position
 */
export function $insertTable(rows: number = 2, columns: number = 2): boolean {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return false;
  }

  const tableNode = $createTableNode();

  // Create rows
  for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
    const rowNode = $createTableRowNode();
    const isHeader = rowIndex === 0;

    // Create columns for this row
    for (let colIndex = 0; colIndex < columns; colIndex++) {
      const cellNode = $createEmptyTableCell(isHeader);
      rowNode.append(cellNode);
    }

    tableNode.append(rowNode);
  }

  // Insert the table at current selection
  $insertNodes([tableNode]);
  return true;
}

/**
 * Inserts a row above the current row
 */
export function $insertRowAbove(): boolean {
  const tableContext = $getTableContext();
  if (!tableContext) {
    return false;
  }

  const { tableNode, rowIndex } = tableContext;
  const rows = tableNode.getChildren() as TableRowNode[];

  if (rows.length === 0) {
    return false;
  }

  // Get the number of columns from the current row
  const currentRow = rows[rowIndex];
  const currentCells = currentRow.getChildren() as TableCellNode[];
  const numColumns = currentCells.length;

  // Create a new row with empty cells
  const newRow = $createTableRowNode();

  for (let i = 0; i < numColumns; i++) {
    // Only make it a header if we're inserting at index 0 (new first row)
    const isHeader = rowIndex === 0;
    const newCell = $createEmptyTableCell(isHeader);
    newRow.append(newCell);
  }

  // Insert the new row
  if (rowIndex === 0) {
    // Inserting at the beginning
    tableNode.getFirstChild()?.insertBefore(newRow);
  } else {
    // Insert before the current row
    currentRow.insertBefore(newRow);
  }

  return true;
}

/**
 * Inserts a row below the current row
 */
export function $insertRowBelow(): TableRowNode | null {
  const tableContext = $getTableContext();
  if (!tableContext) {
    return null;
  }

  const { tableNode, tableRowNode } = tableContext;
  const rows = tableNode.getChildren() as TableRowNode[];

  if (rows.length === 0) {
    return null;
  }

  // Get the number of columns from the current row
  const currentCells = tableRowNode.getChildren() as TableCellNode[];
  const numColumns = currentCells.length;

  // Create a new row with empty cells (never a header when inserting below)
  const newRow = $createTableRowNode();

  for (let i = 0; i < numColumns; i++) {
    const newCell = $createEmptyTableCell(false);
    newRow.append(newCell);
  }

  // Insert the new row after the current row
  tableRowNode.insertAfter(newRow);

  return newRow;
}

/**
 * Focuses the first cell of a given table row
 */
export function $focusFirstCellInRow(tableRow: TableRowNode): boolean {
  const cells = tableRow.getChildren() as TableCellNode[];
  if (cells.length === 0) {
    return false;
  }

  const firstCell = cells[0];
  const paragraphs = firstCell.getChildren();

  if (paragraphs.length > 0) {
    // Focus at the beginning of the first paragraph in the cell
    const firstParagraph = paragraphs[0];
    firstParagraph.selectStart();
    return true;
  }

  return false;
}

/**
 * Inserts a column to the left of the current column
 */
export function $insertColumnLeft(): boolean {
  const tableContext = $getTableContext();
  if (!tableContext) {
    return false;
  }

  const { tableNode, columnIndex } = tableContext;
  const rows = tableNode.getChildren() as TableRowNode[];

  if (rows.length === 0) {
    return false;
  }

  // Add a cell to each row at the specified column index
  rows.forEach((row, rowIndex) => {
    const cells = row.getChildren() as TableCellNode[];
    const isHeader = rowIndex === 0;
    const newCell = $createEmptyTableCell(isHeader);

    if (columnIndex === 0 && cells.length > 0) {
      // Insert at the beginning
      cells[0].insertBefore(newCell);
    } else if (columnIndex > 0 && cells[columnIndex - 1]) {
      // Insert after the previous cell
      cells[columnIndex - 1].insertAfter(newCell);
    } else if (cells.length > 0) {
      // Fallback: append at the end
      row.append(newCell);
    }
  });

  return true;
}

/**
 * Inserts a column to the right of the current column
 */
export function $insertColumnRight(): boolean {
  const tableContext = $getTableContext();
  if (!tableContext) {
    return false;
  }

  const { tableNode, columnIndex } = tableContext;
  const rows = tableNode.getChildren() as TableRowNode[];

  if (rows.length === 0) {
    return false;
  }

  // Add a cell to each row after the specified column index
  rows.forEach((row, rowIndex) => {
    const cells = row.getChildren() as TableCellNode[];
    const isHeader = rowIndex === 0;
    const newCell = $createEmptyTableCell(isHeader);

    if (columnIndex < cells.length && cells[columnIndex]) {
      // Insert after the current cell
      cells[columnIndex].insertAfter(newCell);
    } else {
      // Append at the end if column index is out of bounds
      row.append(newCell);
    }
  });

  return true;
}

/**
 * Deletes the current row (if table has more than 1 row)
 */
export function $deleteRow(): boolean {
  const tableContext = $getTableContext();
  if (!tableContext) {
    return false;
  }

  const { tableNode, tableRowNode } = tableContext;
  const rows = tableNode.getChildren() as TableRowNode[];

  // Don't delete if it's the only row
  if (rows.length <= 1) {
    return false;
  }

  tableRowNode.remove();
  return true;
}

/**
 * Deletes the current column (if table has more than 1 column)
 */
export function $deleteColumn(): boolean {
  const tableContext = $getTableContext();
  if (!tableContext) {
    return false;
  }

  const { tableNode, columnIndex } = tableContext;
  const rows = tableNode.getChildren() as TableRowNode[];

  if (rows.length === 0) {
    return false;
  }

  // Check if any row has more than 1 column
  const hasMultipleColumns = rows.some(row => {
    const cells = row.getChildren() as TableCellNode[];
    return cells.length > 1;
  });

  if (!hasMultipleColumns) {
    return false;
  }

  // Remove the cell at the specified column index from each row
  rows.forEach(row => {
    const cells = row.getChildren() as TableCellNode[];
    if (cells[columnIndex]) {
      cells[columnIndex].remove();
    }
  });

  return true;
}
